// @ts-nocheck
import _ from 'lodash';
import { useState } from 'react';
import PropTypes from 'prop-types';
import omitEmpty from 'omit-empty';
import {
  makeStyles,
  Box,
  Button,
  Divider,
  FormControlLabel,
  Grid,
  Icon,
  Switch,
  TextField,
  Tooltip,
  Typography,
  MenuItem,
  InputAdornment,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { useDialog, useIfMounted } from '../../../../../common/hooks';
import { DateUtils } from '../../../../../common/utils';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    padding: theme.spacing(6, 3, 4, 3),
  },
  form: {
    flex: '1',
    display: 'flex',
    '& .MuiFormControl-root': {
      height: '78px',
    },
  },
  switch: {
    height: '56px',
  },
  flex: {
    display: 'flex',
  },
  icon: {
    marginTop: '-1px',
    marginBottom: '3px',
  },
  divider: {
    height: 'calc(100% - 20px)',
    marginLeft: theme.spacing(0.5),
  },
  blocked: {
    marginTop: theme.spacing(1),
    paddingLeft: theme.spacing(3),
  },
  button: {
    justifyContent: 'flex-start',
    minWidth: '96px',
  },
}));

function CustomerIdentityDetails(props) {
  const [editable, setEditable] = useState(false);
  const [t] = useTranslation();
  const { control, errors, formState, reset, register, handleSubmit } = useForm();
  const { showDialog } = useDialog();
  const classes = useStyles();
  const ifMounted = useIfMounted();

  const {
    customer,
    regions,
    onSaveCustomer,
    onSendVerificationEmail,
    onSendResetPasswordEmail,
    onResetPassword,
    onLogoutAllDevices,
    onCloseCustomer,
    onReopenCustomer,
    onRemoveBlocks,
    onSkipMfa,
    actionsDisabled,
    closed,
    reopened,
  } = props;

  const { isDirty, isSubmitting } = formState;

  const onSubmit = async data => {
    const formData = omitEmpty(data);
    formData.region = regions.find(region => region.value === data.region);
    try {
      await onSaveCustomer(formData);
      ifMounted(() => {
        reset(data);
        setEditable(false);
      });
    } catch (e) {
      // Error already handled
    }
  };

  const handleCancel = () => {
    reset();
    setEditable(false);
  };

  const handleShowSendVerificationEmailDialog = () => {
    showDialog({
      title: t('common.important'),
      content: <Typography>{t('customer.customerSendVerificationEmailConfirm')}</Typography>,
      onConfirm: onSendVerificationEmail,
    });
  };

  const handleShowSendResetPasswordEmailDialog = () => {
    showDialog({
      title: t('common.important'),
      content: <Typography>{t('customer.customerSendResetPasswordEmailConfirm')}</Typography>,
      onConfirm: onSendResetPasswordEmail,
    });
  };

  const showNewPasswordDialog = (newPassword: string) => {
    showDialog({
      title: t('customer.customerNewPassword'),
      content: ({ onCancel }) => {
        return (
          <Box className={classes.dialogContent}>
            <Typography>{t('customer.customerNewPasswordSuccess')}</Typography>
            <br />
            <Typography variant="h6">
              {t('customer.customerNewPasswordInfo')}: <b>{newPassword}</b>
            </Typography>
            <Box display="flex" justifyContent="flex-end">
              <Button
                id="CustomerIdentityDetails-newPasswordOk"
                color="primary"
                startIcon={<Icon>task_alt</Icon>}
                onClick={onCancel}
              >
                <Typography variant="button" noWrap>
                  OK
                </Typography>
              </Button>
            </Box>
          </Box>
        );
      },
      hideFooter: true,
    });
  };

  const handleShowResetPasswordDialog = () => {
    showDialog({
      title: t('common.important'),
      content: <Typography>{t('customer.customerResetPasswordConfirm')}</Typography>,
      onConfirm: async () => {
        const newPassword = await onResetPassword();

        // Show new dialog after closing this one
        return () => {
          showNewPasswordDialog(newPassword);
        };
      },
    });
  };

  const handleShowResetPasswordMethodDialog = () => {
    showDialog({
      title: t('customer.customerResetPassword'),
      content: (
        <Box className={classes.dialogContent}>
          <Typography>{t('customer.customerResetPasswordMethod')}</Typography>
          <br />
          <Button
            id="CustomerIdentityDetails-sendResetPasswordEmail"
            className={classes.button}
            color="primary"
            startIcon={<Icon>send</Icon>}
            onClick={handleShowSendResetPasswordEmailDialog}
            fullWidth
          >
            <Typography variant="button" noWrap>
              {t('customer.customerSendResetPasswordEmail')}
            </Typography>
          </Button>
          <br />
          <Button
            id="CustomerIdentityDetails-resetPassword"
            className={classes.button}
            color="primary"
            startIcon={<Icon>task_alt</Icon>}
            onClick={handleShowResetPasswordDialog}
            fullWidth
          >
            <Typography variant="button" noWrap>
              {t('customer.customerCreateNewPassword')}
            </Typography>
          </Button>
        </Box>
      ),
      hideFooter: true,
    });
  };

  const handleShowLogoutAllDevicesDialog = () => {
    showDialog({
      title: t('common.important'),
      content: <Typography>{t('customer.customerLogoutAllDevicesConfirm')}</Typography>,
      onConfirm: onLogoutAllDevices,
    });
  };

  const handleShowCloseCustomerDialog = () => {
    showDialog({
      title: t('common.important'),
      content: <Typography>{t('customer.customerCloseConfirm')}</Typography>,
      onConfirm: onCloseCustomer,
    });
  };

  const handleShowReopenCustomerDialog = () => {
    showDialog({
      title: t('common.important'),
      content: <Typography>{t('customer.customerReopenConfirm')}</Typography>,
      onConfirm: onReopenCustomer,
    });
  };

  const handleShowRemoveBlocksDialog = () => {
    showDialog({
      title: t('common.important'),
      content: <Typography>{t('customer.customerRemoveBlocksConfirm')}</Typography>,
      onConfirm: onRemoveBlocks,
    });
  };

  const handleShowSkipMfaDialog = () => {
    showDialog({
      title: t('common.important'),
      content: <Typography>{t('customer.customerSkipMfaConfirm')}</Typography>,
      onConfirm: onSkipMfa,
    });
  };

  const isUserClosed = customer.status === 'Lukket' && !reopened || closed;

  return (
    <Box id="CustomerIdentityDetails-container" className={classes.container}>
      <form
        id="CustomerIdentityDetails-form"
        className={classes.form}
        onSubmit={handleSubmit(onSubmit)}
      >
        <Grid container spacing={4} justify="space-between">
          <Grid container item xs={8} spacing={2}>
            <Grid item xs={10}>
              <TextField
                id="CustomerIdentityDetails-userId"
                name="userId"
                label={t('user.userId')}
                variant="outlined"
                defaultValue={customer.userId}
                InputLabelProps={{ shrink: true }}
                inputRef={register}
                disabled
                fullWidth
              />
            </Grid>
            <Grid item xs={2}>
              <FormControlLabel
                className={classes.switch}
                label={t('user.userTestUser')}
                control={
                  <Controller
                    name="testUser"
                    control={control}
                    defaultValue={customer.testUser}
                    render={params => (
                      <Switch
                        id="CustomerIdentityDetails-testUser"
                        checked={params.value}
                        onChange={e => params.onChange(e.target.checked)}
                        disabled={!editable}
                      />
                    )}
                  />
                }
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                id="CustomerIdentityDetails-firstname"
                name="firstname"
                label={t('user.userFirstName')}
                variant="outlined"
                defaultValue={customer.firstname}
                InputLabelProps={{ shrink: true }}
                inputRef={register({
                  required: t('user.userFirstNameRequired'),
                })}
                error={editable && !!errors.firstname}
                helperText={editable && errors.firstname?.message}
                disabled={!editable}
                fullWidth
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                id="CustomerIdentityDetails-email"
                name="email"
                label={t('user.userEmail')}
                variant="outlined"
                defaultValue={customer.email}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip
                        title={
                          customer.emailVerified
                            ? t('user.userEmailVerified')
                            : t('user.userEmailNotVerified')
                        }
                      >
                        <Icon>{customer.emailVerified ? 'verified_user' : 'remove_moderator'}</Icon>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                InputLabelProps={{ shrink: true }}
                inputRef={register({
                  required: t('user.userEmailRequired'),
                  pattern: {
                    value:
                      /^(([^<>()\\.,;:\s@"]+(\.[^<>()\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                    message: t('user.userEmailInvalid'),
                  },
                })}
                error={editable && !!errors.email}
                helperText={editable && errors.email?.message}
                disabled={!editable}
                fullWidth
              />
            </Grid>
            <Grid item xs={2}>
              <Controller
                name="sex"
                control={control}
                defaultValue={_.defaultTo(customer.sex, 'U')}
                as={
                  <TextField
                    id="CustomerIdentityDetails-sex"
                    label={t('user.userSex')}
                    variant="outlined"
                    disabled={!editable}
                    fullWidth
                    select
                  >
                    <MenuItem value="M">{t('common.male')}</MenuItem>
                    <MenuItem value="F">{t('common.female')}</MenuItem>
                    <MenuItem value="U">{t('common.unknown')}</MenuItem>
                  </TextField>
                }
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                id="CustomerIdentityDetails-cellphone"
                name="cellphone"
                label={t('user.userCellPhone')}
                variant="outlined"
                defaultValue={customer.cellphone}
                InputLabelProps={{ shrink: true }}
                inputRef={register({
                  pattern: {
                    value: /^[0-9+()#.\s/ext-]+$/,
                    message: t('user.userCellPhoneInvalid'),
                  },
                })}
                error={editable && !!errors.cellphone}
                helperText={editable && errors.cellphone?.message}
                disabled={!editable}
                fullWidth
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                id="CustomerIdentityDetails-loginSource"
                name="source"
                label={t('user.userLoginSource')}
                variant="outlined"
                defaultValue={customer.source}
                InputLabelProps={{ shrink: true }}
                inputRef={register}
                disabled
                fullWidth
              />
            </Grid>
            <Grid item xs={4}>
              <Controller
                name="region"
                control={control}
                defaultValue={customer.region?.value}
                as={
                  <TextField
                    id="CustomerIdentityDetails-region"
                    label={t('user.userRegion')}
                    variant="outlined"
                    disabled={!editable}
                    fullWidth
                    select
                  >
                    {regions.map(region => (
                      <MenuItem key={region.value} value={region.value}>
                        {region.displayValue}
                      </MenuItem>
                    ))}
                  </TextField>
                }
              />
            </Grid>
            <Grid item xs={2}>
              <TextField
                id="CustomerIdentityDetails-birthYear"
                name="birthday"
                label={t('user.userBirthYear')}
                variant="outlined"
                defaultValue={DateUtils.getYear(customer.birthday)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ maxLength: 4 }}
                inputRef={register({
                  required: t('user.userBirthYearRequired'),
                  pattern: {
                    value: /^[12][0-9]{3}$/,
                    message: t('user.userBirthYearInvalid'),
                  },
                  min: {
                    value: 1890,
                    message: t('user.userBirthYearMin'),
                  },
                })}
                error={editable && !!errors.birthday}
                helperText={editable && errors.birthday?.message}
                disabled={!editable}
                fullWidth
              />
            </Grid>
          </Grid>
          <Divider className={classes.divider} orientation="vertical" />
          <Grid container item xs={2}>
            {customer.created ? (
              <>
                <Grid item xs={12}>
                  <Typography id="CustomerIdentityDetails-status" variant="button">
                    {`${t('user.userStatus')}: ${customer.blocked}`}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography id="CustomerIdentityDetails-createdLabel" className={classes.flex}>
                    <Icon className={classes.icon}>more_time</Icon>
                    &nbsp;
                    {`${t('user.userCreated')}:`}
                  </Typography>
                  <Typography id="CustomerIdentityDetails-createdValue">
                    {DateUtils.getDateTime(customer.created)}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    id="CustomerIdentityDetails-lastUpdatedLabel"
                    className={classes.flex}
                  >
                    <Icon className={classes.icon}>update</Icon>
                    &nbsp;
                    {`${t('user.userUpdated')}:`}
                  </Typography>
                  <Typography id="CustomerIdentityDetails-lastUpdatedValue">
                    {DateUtils.getDateTime(customer.lastUpdated)}
                  </Typography>
                </Grid>
                {customer.lastLoginTime && (
                  <Grid item xs={12}>
                    <Typography
                      id="CustomerIdentityDetails-lastLoginLabel"
                      className={classes.flex}
                    >
                      <Icon className={classes.icon}>access_time</Icon>
                      &nbsp;
                      {`${t('user.userLastLogin')}:`}
                    </Typography>
                    <Typography id="CustomerIdentityDetails-lastLoginValue">
                      {DateUtils.getDateTime(customer.lastLoginTime)}
                    </Typography>
                  </Grid>
                )}
              </>
            ) : (
              <Grid item xs={12}>
                <Typography id="CustomerIdentityDetails-notLoggedIn" className={classes.flex}>
                  <Icon className={classes.icon}>info</Icon>
                  &nbsp;
                  {t('user.userNotLoggedIn')}
                </Typography>
              </Grid>
            )}
            {customer.blockedStatus && (
              <Grid item xs={12}>
                <Typography
                  id="CustomerIdentityDetails-allDevicesBlocked"
                  color="error"
                  className={classes.flex}
                >
                  <Icon className={classes.icon}>blocked</Icon>
                  &nbsp;
                  {t('user.userDevicesBlocked')}
                </Typography>
              </Grid>
            )}
            {!_.isEmpty(customer.blockedFor) && (
              <Grid item xs={12}>
                <Typography
                  id="CustomerIdentityDetails-blockedFrom"
                  color="error"
                  className={classes.flex}
                >
                  <Icon className={classes.icon}>privacy_tip</Icon>
                  &nbsp;
                  {`${t('user.userBlockedFrom')}:`}
                </Typography>
                {customer.blockedFor.map((blocked, index) => (
                  <Typography
                    key={blocked.ip}
                    id={`CustomerIdentityDetails-blockedFrom-${index}`}
                    color="error"
                    className={classes.blocked}
                  >
                    {`- ${blocked.ip}`}
                  </Typography>
                ))}
              </Grid>
            )}
          </Grid>
          <Divider className={classes.divider} orientation="vertical" />
          <Grid container item xs={2}>
            {editable ? (
              <>
                <Grid item xs={6}>
                  <Button
                    id="CustomerIdentityDetails-save"
                    className={classes.button}
                    type="submit"
                    color="primary"
                    startIcon={<Icon>save</Icon>}
                    disabled={actionsDisabled || isSubmitting || !isDirty}
                    fullWidth
                  >
                    <Typography variant="button" noWrap>
                      {t('common.save')}
                    </Typography>
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    id="CustomerIdentityDetails-cancel"
                    className={classes.button}
                    color="primary"
                    startIcon={<Icon>cancel</Icon>}
                    onClick={handleCancel}
                    disabled={actionsDisabled}
                    fullWidth
                  >
                    <Typography variant="button" noWrap>
                      {t('common.cancel')}
                    </Typography>
                  </Button>
                </Grid>
              </>
            ) : (
              <Grid item xs={12}>
                <Button
                  id="CustomerIdentityDetails-editCustomer"
                  className={classes.button}
                  color="primary"
                  startIcon={<Icon>edit</Icon>}
                  onClick={() => setEditable(true)}
                  disabled={actionsDisabled || isUserClosed}
                  fullWidth
                >
                  <Typography variant="button" noWrap>
                    {t('customer.customerEdit')}
                  </Typography>
                </Button>
              </Grid>
            )}
            <Grid item xs={12}>
              <Button
                id="CustomerIdentityDetails-verifyEmail"
                className={classes.button}
                color="primary"
                startIcon={<Icon>send</Icon>}
                onClick={handleShowSendVerificationEmailDialog}
                disabled={actionsDisabled || isUserClosed}
                fullWidth
              >
                <Typography variant="button" noWrap>
                  {t('customer.customerSendVerificationEmail')}
                </Typography>
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Button
                id="CustomerIdentityDetails-resetPasswordMethod"
                className={classes.button}
                color="primary"
                startIcon={<Icon>settings_backup_restore</Icon>}
                onClick={handleShowResetPasswordMethodDialog}
                disabled={actionsDisabled || isUserClosed}
                fullWidth
              >
                <Typography variant="button" noWrap>
                  {t('customer.customerResetPassword')}
                </Typography>
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Button
                id="CustomerIdentityDetails-skipMfa"
                className={classes.button}
                color="primary"
                startIcon={<Icon>key_off</Icon>}
                onClick={handleShowSkipMfaDialog}
                disabled={actionsDisabled || isUserClosed}
                fullWidth
              >
                <Typography variant="button" noWrap>
                  {t('customer.customerSkipMfa')}
                </Typography>
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Button
                id="CustomerIdentityDetails-forceLogout"
                className={classes.button}
                color="primary"
                startIcon={<Icon>login</Icon>}
                onClick={handleShowLogoutAllDevicesDialog}
                disabled={actionsDisabled || isUserClosed}
                fullWidth
              >
                <Typography variant="button" noWrap>
                  {t('customer.customerLogoutAllDevices')}
                </Typography>
              </Button>
            </Grid>
            <Grid item xs={12}>
              {isUserClosed ? (
                <Button
                  id="CustomerIdentityDetails-reopenCustomer"
                  className={classes.button}
                  color="primary"
                  startIcon={<Icon>lock_open</Icon>}
                  onClick={handleShowReopenCustomerDialog}
                  disabled={actionsDisabled}
                  fullWidth
                >
                  <Typography variant="button" noWrap>
                    {t('customer.customerReopen')}
                  </Typography>
                </Button>
              ) : (
                <Button
                  id="CustomerIdentityDetails-closeCustomer"
                  className={classes.button}
                  color="primary"
                  startIcon={<Icon>lock</Icon>}
                  onClick={handleShowCloseCustomerDialog}
                  disabled={actionsDisabled}
                  fullWidth
                >
                  <Typography variant="button" noWrap>
                    {t('customer.customerClose')}
                  </Typography>
                </Button>
              )}
            </Grid>
            {!_.isEmpty(customer.blockedFor) && (
              <Grid item xs={12}>
                <Button
                  id="CustomerIdentityDetails-removeBlocks"
                  className={classes.button}
                  color="primary"
                  startIcon={<Icon>remove_circle_outline</Icon>}
                  onClick={handleShowRemoveBlocksDialog}
                  disabled={actionsDisabled}
                  fullWidth
                >
                  <Typography variant="button" noWrap>
                    {t('customer.customerRemoveBlocks')}
                  </Typography>
                </Button>
              </Grid>
            )}
          </Grid>
        </Grid>
      </form>
    </Box>
  );
}

CustomerIdentityDetails.propTypes = {
  customer: PropTypes.shape({
    userId: PropTypes.string,
    email: PropTypes.string,
    firstname: PropTypes.string,
    cellphone: PropTypes.string,
    birthday: PropTypes.string,
    sex: PropTypes.string,
    source: PropTypes.string,
    created: PropTypes.string,
    lastUpdated: PropTypes.string,
    lastLoginTime: PropTypes.string,
    blocked: PropTypes.string,
    status: PropTypes.string,
    blockedFor: PropTypes.arrayOf(
      PropTypes.shape({
        ip: PropTypes.string,
      })
    ),
    blockedStatus: PropTypes.bool,
    emailVerified: PropTypes.bool,
    testUser: PropTypes.bool,
    region: PropTypes.shape({
      displayValue: PropTypes.string,
      value: PropTypes.string,
    }),
  }).isRequired,
  regions: PropTypes.arrayOf(
    PropTypes.shape({
      displayValue: PropTypes.string,
      value: PropTypes.string,
    })
  ).isRequired,
  onSaveCustomer: PropTypes.func.isRequired,
  onSendVerificationEmail: PropTypes.func.isRequired,
  onSendResetPasswordEmail: PropTypes.func.isRequired,
  onResetPassword: PropTypes.func.isRequired,
  onLogoutAllDevices: PropTypes.func.isRequired,
  onCloseCustomer: PropTypes.func.isRequired,
  onReopenCustomer: PropTypes.func.isRequired,
  onRemoveBlocks: PropTypes.func.isRequired,
  onSkipMfa: PropTypes.func.isRequired,
  actionsDisabled: PropTypes.bool.isRequired,
  closed: PropTypes.bool.isRequired,
  reopened: PropTypes.bool.isRequired,
};

export default CustomerIdentityDetails;
